/**
 * 考试结果页面组件
 * 显示考试成绩和答题详情
 */

import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Trophy,
  CheckCircle,
  XCircle,
  ListChecks,
  ToggleLeft,
  MessageSquare,
} from 'lucide-react';
import type { ExamSubmitResult } from '@/types/api';
import { api } from '@/services/api';
import styles from './ExamPage.module.css';

interface ExamResultPageProps {
  taskId: number;
  userId: number;
  onBack: () => void;
}

// 题目类型配置
const QUESTION_TYPES = [
  { value: 'single', label: '单选题', icon: ListChecks, color: '#3b82f6' },
  { value: 'multiple', label: '多选题', icon: CheckCircle, color: '#8b5cf6' },
  { value: 'multi', label: '多选题', icon: CheckCircle, color: '#8b5cf6' },
  { value: 'judgment', label: '判断题', icon: ToggleLeft, color: '#f59e0b' },
  { value: 'judge', label: '判断题', icon: ToggleLeft, color: '#f59e0b' },
  { value: 'boolean', label: '判断题', icon: ToggleLeft, color: '#f59e0b' },
  { value: 'essay', label: '简答题', icon: MessageSquare, color: '#10b981' },
  { value: 'short', label: '简答题', icon: MessageSquare, color: '#10b981' },
];

// 判断题类型列表
const JUDGMENT_TYPES = ['judgment', 'judge', 'boolean'];

// 判断是否为判断题
const isJudgmentType = (type: string) => JUDGMENT_TYPES.includes(type);

const getQuestionTypeConfig = (type: string) => {
  return QUESTION_TYPES.find(t => t.value === type) || QUESTION_TYPES[0];
};

export default function ExamResultPage({ taskId, userId, onBack }: ExamResultPageProps) {
  const [result, setResult] = useState<ExamSubmitResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [showOnlyWrong, setShowOnlyWrong] = useState(false);

  useEffect(() => {
    const loadResult = async () => {
      try {
        const res = await api.getExamTaskResult(taskId, userId);
        if (res.code === 200 && res.data) {
          setResult(res.data);
        }
      } catch (error) {
        console.error('加载考试结果失败:', error);
      } finally {
        setLoading(false);
      }
    };
    loadResult();
  }, [taskId, userId]);

  const renderQuestion = (record: ExamSubmitResult['records'][0], index: number) => {
    const typeConfig = getQuestionTypeConfig(record.question_type);

    return (
      <div
        key={record.question_id}
        className={`${styles.resultQuestionCard} ${record.is_correct ? styles.correctCard : styles.wrongCard}`}
      >
        <div className={styles.questionHeader}>
          <div className={styles.questionNumber}>
            <span className={styles.numberBadge}>第 {index + 1} 题</span>
            <span
              className={styles.questionType}
              style={{ backgroundColor: `${typeConfig.color}15`, color: typeConfig.color }}
            >
              <typeConfig.icon size={14} />
              {typeConfig.label}
            </span>
            {record.is_correct ? (
              <span className={styles.correctBadge}>
                <CheckCircle size={14} /> 正确
              </span>
            ) : (
              <span className={styles.wrongBadge}>
                <XCircle size={14} /> 错误
              </span>
            )}
          </div>
          <span className={styles.questionScore}>
            {record.score} / {record.max_score} 分
          </span>
        </div>

        <div className={styles.questionContent}>
          <p>{record.question_content}</p>
        </div>

        {/* 选项 */}
        {record.options && (
          <div className={styles.optionsList}>
            {Object.entries(record.options as Record<string, string>)
              .filter(([, value]) => value?.trim())
              .map(([key, value]) => {
                const isStudentAnswer = record.student_answer.includes(key);
                const isCorrectAnswer = record.correct_answer.includes(key);
                const isWrong = isStudentAnswer && !isCorrectAnswer;
                const isMissed = !isStudentAnswer && isCorrectAnswer && !record.is_correct;

                let optionClass = styles.optionItem;
                if (isCorrectAnswer) {
                  optionClass = `${styles.optionItem} ${styles.correctOption}`;
                }
                if (isWrong) {
                  optionClass = `${styles.optionItem} ${styles.wrongOption}`;
                }

                return (
                  <div key={key} className={optionClass}>
                    <span className={styles.optionKey}>{key}.</span>
                    <span className={styles.optionValue}>{value}</span>
                    <div className={styles.optionTags}>
                      {isStudentAnswer && (
                        <span className={styles.yourAnswerTag}>
                          <CheckCircle size={12} /> 你的选择
                        </span>
                      )}
                      {isCorrectAnswer && (
                        <span className={styles.correctAnswerTag}>
                          <CheckCircle size={12} /> 正确答案
                        </span>
                      )}
                      {isWrong && (
                        <XCircle size={14} className={styles.wrongIcon} />
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        )}

        {/* 判断题无选项时自动生成正确/错误选项 */}
        {isJudgmentType(record.question_type) && !record.options && (
          <div className={styles.optionsList}>
            {(['A', 'B'] as const).map((key) => {
              const label = key === 'A' ? '正确' : '错误';
              const isStudentAnswer = record.student_answer === key || record.student_answer === label;
              const isCorrectAnswer = record.correct_answer === label || record.correct_answer === key;
              const isWrong = isStudentAnswer && !isCorrectAnswer;

              let optionClass = styles.optionItem;
              if (isCorrectAnswer) {
                optionClass = `${styles.optionItem} ${styles.correctOption}`;
              }
              if (isWrong) {
                optionClass = `${styles.optionItem} ${styles.wrongOption}`;
              }

              return (
                <div key={key} className={optionClass}>
                  <span className={styles.optionKey}>{key}.</span>
                  <span className={styles.optionValue}>{label}</span>
                  <div className={styles.optionTags}>
                    {isStudentAnswer && (
                      <span className={styles.yourAnswerTag}>
                        <CheckCircle size={12} /> 你的选择
                      </span>
                    )}
                    {isCorrectAnswer && (
                      <span className={styles.correctAnswerTag}>
                        <CheckCircle size={12} /> 正确答案
                      </span>
                    )}
                    {isWrong && (
                      <XCircle size={14} className={styles.wrongIcon} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* 简答题显示答案 */}
        {(record.question_type === 'essay' || record.question_type === 'short') && (
          <div className={styles.essayAnswers}>
            <div className={`${styles.essayAnswer} ${record.is_correct ? styles.essayCorrect : styles.essayWrong}`}>
              <span className={styles.answerLabel}>📝 您的答案：</span>
              <p>{record.student_answer || '未作答'}</p>
            </div>
            <div className={`${styles.essayAnswer} ${styles.essayCorrectAnswer}`}>
              <span className={styles.answerLabel}>✅ 参考答案：</span>
              <p>{record.correct_answer}</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className={styles.resultContainer}>
        <div className={styles.loading}>
          <div className={styles.spinner} style={{ width: 32, height: 32, border: '3px solid #e5e7eb', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          <p>加载中...</p>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className={styles.resultContainer}>
        <div className={styles.error}>
          <XCircle size={48} />
          <p>加载失败</p>
          <button className={styles.backBtn} onClick={onBack}>返回</button>
        </div>
      </div>
    );
  }

  const correctCount = result.correct_count;
  const wrongCount = result.answer_count - correctCount;

  return (
    <div className={styles.resultContainer}>
      {/* 结果头部 */}
      <div className={styles.resultHeader}>
        <button className={styles.backBtn} onClick={onBack}>
          <ArrowLeft size={16} />
          返回任务列表
        </button>
        <h1 className={styles.resultTitle}>考试结果</h1>
      </div>

      {/* 成绩卡片 */}
      <div className={`${styles.scoreCard} ${result.is_passed ? styles.passed : styles.failed}`}>
        <div className={styles.scoreLeft}>
          <div className={styles.scoreCircle}>
            <Trophy size={28} className={styles.trophyIcon} />
            <span className={styles.scoreValue}>{result.total_score}</span>
            <span className={styles.scoreTotal}>/{result.max_score}</span>
          </div>
          <div className={styles.scoreInfo}>
            <h2>{result.is_passed ? '恭喜通过！' : '未通过'}</h2>
            <p>及格线 {result.pass_score} 分</p>
          </div>
        </div>
        <div className={styles.scoreRight}>
          <div className={styles.statItem}>
            <span className={styles.statValue}>{result.answer_count}</span>
            <span className={styles.statLabel}>总题数</span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statValue + ' ' + styles.correctText}>{correctCount}</span>
            <span className={styles.statLabel}>正确</span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statValue + ' ' + styles.wrongText}>{wrongCount}</span>
            <span className={styles.statLabel}>错误</span>
          </div>
        </div>
      </div>

      {/* 题目详情列表 */}
      <div className={styles.questionsList}>
        <div className={styles.listTitleRow}>
          <h3 className={styles.listTitle}>答题详情</h3>
          <button
            className={`${styles.wrongFilterBtn} ${showOnlyWrong ? styles.wrongFilterActive : ''}`}
            onClick={() => setShowOnlyWrong(prev => !prev)}
          >
            <XCircle size={14} />
            仅看错题
          </button>
        </div>
        <div className={styles.questionsScroll}>
          {result.records
            .map((record, index) => ({ record, index }))
            .filter(({ record }) => !showOnlyWrong || !record.is_correct)
            .map(({ record, index }) => renderQuestion(record, index))}
          {result.records.filter(record => !record.is_correct).length === 0 && showOnlyWrong && (
            <div className={styles.noWrong}>全部正确，没有错题！</div>
          )}
        </div>
      </div>

      {/* 底部操作栏 */}
      <div className={styles.resultFooter}>
        <button className={styles.finishBtn} onClick={onBack}>
          完成并返回
        </button>
      </div>
    </div>
  );
}